import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Users, Search as SearchIcon, ChevronRight, X, Calendar, Briefcase, Phone, Mail, Database, ListChecks, Target, Download, Crown, CheckCircle2, Send } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return s; }
};

export default function AdminUsers() {
  const { authHeaders, user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [q, setQ] = useState("");
  const [includeGuests, setIncludeGuests] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // user object whose detail is open
  const [detailTab, setDetailTab] = useState("profile"); // profile | searches | goals
  const [searches, setSearches] = useState([]);
  const [goals, setGoals] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const [s, u] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: authHeaders() }),
        axios.get(`${API}/admin/users`, {
          headers: authHeaders(),
          params: { include_guests: includeGuests, q: q || undefined, limit: 300 },
        }),
      ]);
      setStats(s.data);
      setUsers(u.data);
    } catch {
      toast.error("Failed to load admin data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [includeGuests]);
  useEffect(() => {
    const t = setTimeout(refresh, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const openDetail = async (u) => {
    setSelected(u);
    setDetailTab("profile");
    setSearches([]); setGoals([]);
    setDetailLoading(true);
    try {
      const [s, g] = await Promise.all([
        axios.get(`${API}/admin/users/${u.id}/searches`, { headers: authHeaders() }),
        axios.get(`${API}/admin/users/${u.id}/goals`, { headers: authHeaders() }),
      ]);
      setSearches(s.data); setGoals(g.data);
    } catch { /* ignore */ } finally { setDetailLoading(false); }
  };

  const toggleSubscription = async () => {
    if (!selected) return;
    const next = !selected.is_subscribed;
    try {
      const res = await axios.patch(`${API}/admin/users/${selected.id}`, { is_subscribed: next }, { headers: authHeaders() });
      setSelected(res.data);
      setUsers((prev) => prev.map((u) => (u.id === res.data.id ? res.data : u)));
      toast.success(next ? `${res.data.name} is now subscribed` : `${res.data.name} subscription removed`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update subscription");
    }
  };

  const [resendBusy, setResendBusy] = useState(false);
  const resendWelcome = async () => {
    if (!selected || selected.is_guest) return;
    setResendBusy(true);
    try {
      const res = await axios.post(`${API}/admin/users/${selected.id}/resend-welcome`, {}, { headers: authHeaders() });
      if (res.data?.email_enabled) {
        toast.success(`Welcome email re-sent to ${res.data.email}`);
      } else {
        toast(`Queued, but RESEND_API_KEY is empty`, {
          description: "Paste your Resend key into backend/.env and restart for the email to actually go out.",
        });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resend welcome email");
    } finally {
      setResendBusy(false);
    }
  };

  const exportCsv = () => {
    const headers = [
      "id","name","email","profession","profession_other","date_of_birth",
      "country_code","phone","is_guest","is_subscribed","email_verified",
      "generations_used","search_count","goal_count","created_at","last_login_at","last_login_ip",
    ];
    const escape = (v) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[,"\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = users.map((u) => [
      u.id, u.name, u.email, u.profession || "", u.profession_other || "", u.date_of_birth || "",
      u.contact_country_code || "", u.contact_phone || "",
      u.is_guest ? "true" : "false",
      u.is_subscribed ? "true" : "false",
      u.email_verified ? "true" : "false",
      u.generations_used, u.search_count, u.goal_count,
      u.created_at || "", u.last_login_at || "", u.last_login_ip || "",
    ].map(escape).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    a.href = url; a.download = `commpass-users-${stamp}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${users.length} users to CSV`);
  };

  const statCards = useMemo(() => ([
    { label: "Users", v: stats.users || 0 },
    { label: "Real users", v: stats.real_users || 0 },
    { label: "Guests", v: stats.guests || 0 },
    { label: "Searches", v: stats.searches || 0 },
    { label: "Goal banks", v: stats.goals || 0 },
  ]), [stats]);

  if (user?.role !== "admin") {
    return <div className="max-w-4xl mx-auto px-6 py-20 text-center text-[#52606D]" data-testid="admin-users-denied">Admin access only.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-14" data-testid="admin-users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">Admin · User review</span>
          <h1 className="font-display text-4xl font-semibold text-[#2C4C3B] mt-1">Logins, searches &amp; goals</h1>
          <p className="text-[#52606D] mt-2 text-sm">Every clinician who has signed up and what they've generated. Tap a row for the full profile and history.</p>
        </div>
        <a href="/admin" className="text-sm text-[#2C4C3B] hover:underline" data-testid="link-content-admin">Content admin →</a>
      </div>

      <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {statCards.map((s) => (
          <div key={s.label} className="p-4 rounded-2xl bg-white border border-[#E2E8F0]" data-testid={`admin-stat-${s.label.toLowerCase().replace(/\s+/g,'-')}`}>
            <div className="text-[10px] tracking-[0.15em] uppercase text-[#52606D]">{s.label}</div>
            <div className="font-display text-2xl text-[#2C4C3B] mt-1">{s.v}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 p-5 rounded-2xl bg-white border border-[#E2E8F0]">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52606D]" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, profession or phone…" className="pl-9 rounded-full" data-testid="admin-users-search" />
          </div>
          <label className="flex items-center gap-2 text-sm text-[#52606D]">
            <input type="checkbox" checked={includeGuests} onChange={(e) => setIncludeGuests(e.target.checked)} data-testid="admin-include-guests" />
            Include guests
          </label>
          <button onClick={exportCsv} disabled={users.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium bg-[#2C4C3B] hover:bg-[#1F3A2C] text-white disabled:opacity-50"
            data-testid="admin-users-export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </button>
        </div>

        <div className="mt-6 overflow-x-auto" data-testid="admin-users-table-wrap">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] tracking-[0.15em] uppercase text-[#52606D] border-b border-[#E2E8F0]">
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Email</th>
                <th className="px-3 py-2">Profession</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Joined</th>
                <th className="px-3 py-2">Last login</th>
                <th className="px-3 py-2">Gens</th>
                <th className="px-3 py-2">Searches</th>
                <th className="px-3 py-2">Goals</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-[#52606D]" data-testid="admin-users-loading">Loading…</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-[#52606D]" data-testid="admin-users-empty">No users match.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#E2E8F0] hover:bg-[#FDFCF8]" data-testid={`admin-user-row-${u.id}`}>
                  <td className="px-3 py-3 font-medium text-[#2C4C3B]">
                    {u.name}
                    {u.is_guest && <span className="ml-2 text-[10px] tracking-[0.15em] uppercase bg-[#F4F5F0] text-[#52606D] px-2 py-0.5 rounded-full">guest</span>}
                    {u.role === "admin" && <span className="ml-2 text-[10px] tracking-[0.15em] uppercase bg-[#E6F0E9] text-[#2C4C3B] px-2 py-0.5 rounded-full">admin</span>}
                    {u.is_subscribed && <span className="ml-2 text-[10px] tracking-[0.15em] uppercase bg-[#C66A51]/15 text-[#C66A51] px-2 py-0.5 rounded-full">subscribed</span>}
                  </td>
                  <td className="px-3 py-3 text-[#1A202C]">{u.email}</td>
                  <td className="px-3 py-3 text-[#52606D]">{u.profession || "—"}{u.profession === "Other" && u.profession_other ? ` (${u.profession_other})` : ""}</td>
                  <td className="px-3 py-3 text-[#52606D]">{u.contact_country_code ? `${u.contact_country_code} ${u.contact_phone || ""}` : "—"}</td>
                  <td className="px-3 py-3 text-[#52606D]">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-3 text-[#52606D]">{fmtDate(u.last_login_at)}</td>
                  <td className="px-3 py-3 text-[#2C4C3B] font-semibold">{u.generations_used}</td>
                  <td className="px-3 py-3 text-[#2C4C3B]">{u.search_count}</td>
                  <td className="px-3 py-3 text-[#2C4C3B]">{u.goal_count}</td>
                  <td className="px-3 py-3">
                    <button onClick={() => openDetail(u)} className="inline-flex items-center text-[#C66A51] hover:text-[#A85639] text-sm font-medium" data-testid={`admin-user-open-${u.id}`}>
                      Open <ChevronRight className="w-4 h-4 ml-1" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail drawer / modal */}
      {selected && (
        <div className="fixed inset-0 z-[80] flex items-stretch justify-end bg-black/40" data-testid="admin-user-detail" onClick={() => setSelected(null)}>
          <div className="bg-white w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-[#E2E8F0] flex items-start justify-between gap-4">
              <div>
                <span className="text-xs tracking-[0.2em] uppercase text-[#C66A51]">User profile</span>
                <h2 className="font-display text-2xl text-[#2C4C3B] mt-1">{selected.name}</h2>
                <div className="text-sm text-[#52606D]">{selected.email}</div>
              </div>
              <button onClick={() => setSelected(null)} className="w-9 h-9 rounded-full bg-[#F4F5F0] hover:bg-[#E6F0E9] flex items-center justify-center" data-testid="admin-user-detail-close">
                <X className="w-4 h-4 text-[#52606D]" />
              </button>
            </div>

            <div className="px-6 pt-4">
              <div className="grid grid-cols-3 gap-2 bg-[#F4F5F0] rounded-full p-1">
                {[
                  { code: "profile", label: "Profile" },
                  { code: "searches", label: `Searches (${searches.length})` },
                  { code: "goals", label: `Goals (${goals.length})` },
                ].map((t) => (
                  <button key={t.code} onClick={() => setDetailTab(t.code)}
                    className={`text-sm py-2 rounded-full font-medium transition-colors ${detailTab === t.code ? "bg-white text-[#2C4C3B] shadow-sm" : "text-[#52606D]"}`}
                    data-testid={`admin-detail-tab-${t.code}`}>
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {detailTab === "profile" && (
              <div className="p-6 space-y-3" data-testid="admin-detail-profile">
                <div className="p-4 rounded-2xl bg-[#FDF6F0] border border-[#C66A51]/30 flex items-start justify-between gap-3 flex-wrap" data-testid="admin-detail-subscription">
                  <div className="flex items-start gap-3">
                    <Crown className="w-5 h-5 text-[#C66A51] mt-0.5" />
                    <div>
                      <div className="text-xs tracking-[0.15em] uppercase text-[#52606D]">Subscription</div>
                      <div className="text-sm text-[#1A202C] mt-0.5">
                        {selected.is_subscribed ? <span className="font-semibold text-[#2C4C3B]">Active (full access)</span> : <span className="text-[#52606D]">Trial only · {selected.generations_used} of 1 used</span>}
                      </div>
                      {selected.subscription_changed_at && (
                        <div className="text-[10px] text-[#52606D] mt-1">Last changed {fmtDate(selected.subscription_changed_at)}</div>
                      )}
                    </div>
                  </div>
                  <button onClick={toggleSubscription}
                    className={`px-4 py-2 rounded-full text-sm font-medium ${selected.is_subscribed ? "bg-white border border-[#C66A51] text-[#C66A51]" : "bg-[#C66A51] hover:bg-[#A85639] text-white"}`}
                    data-testid="admin-toggle-subscription">
                    {selected.is_subscribed ? "Revoke access" : "Mark as subscribed"}
                  </button>
                </div>
                <DetailRow icon={CheckCircle2} label="Email verified" value={selected.email_verified ? "Yes" : "No"} />
                {!selected.is_guest && (
                  <div className="p-4 rounded-2xl bg-white border border-[#E2E8F0] flex items-start justify-between gap-3 flex-wrap" data-testid="admin-detail-resend-welcome">
                    <div className="flex items-start gap-3">
                      <Send className="w-5 h-5 text-[#2C4C3B] mt-0.5" />
                      <div>
                        <div className="text-xs tracking-[0.15em] uppercase text-[#52606D]">Welcome email</div>
                        <div className="text-sm text-[#1A202C] mt-0.5">
                          Re-send the welcome + fresh verification link.
                        </div>
                        {selected.last_welcome_resent_at && (
                          <div className="text-[10px] text-[#52606D] mt-1">Last re-sent {fmtDate(selected.last_welcome_resent_at)}</div>
                        )}
                      </div>
                    </div>
                    <button onClick={resendWelcome} disabled={resendBusy}
                      className="px-4 py-2 rounded-full text-sm font-medium bg-white border border-[#2C4C3B] text-[#2C4C3B] hover:bg-[#E6F0E9] disabled:opacity-50"
                      data-testid="admin-resend-welcome-btn">
                      {resendBusy ? "Sending…" : "Resend welcome"}
                    </button>
                  </div>
                )}
                <DetailRow icon={Briefcase} label="Profession" value={(selected.profession || "—") + (selected.profession === "Other" && selected.profession_other ? ` (${selected.profession_other})` : "")} />
                <DetailRow icon={Calendar} label="Date of birth" value={selected.date_of_birth || "—"} />
                <DetailRow icon={Mail} label="Email" value={selected.email} />
                <DetailRow icon={Phone} label="Contact" value={selected.contact_country_code ? `${selected.contact_country_code} ${selected.contact_phone || ""}` : "—"} />
                <DetailRow icon={Calendar} label="Signed up" value={fmtDate(selected.created_at)} />
                <DetailRow icon={Calendar} label="Last login" value={fmtDate(selected.last_login_at)} />
                <DetailRow icon={Database} label="Last login IP" value={selected.last_login_ip || "—"} />
                <DetailRow icon={Database} label="Last login device" value={selected.last_login_user_agent || "—"} mono />
                <DetailRow icon={ListChecks} label="Generations used" value={String(selected.generations_used)} />
              </div>
            )}

            {detailTab === "searches" && (
              <div className="p-6 space-y-3" data-testid="admin-detail-searches">
                {detailLoading && <div className="text-[#52606D]">Loading…</div>}
                {!detailLoading && searches.length === 0 && <div className="text-[#52606D]" data-testid="admin-detail-searches-empty">No searches yet.</div>}
                {searches.map((s) => (
                  <div key={s.id} className="p-4 rounded-2xl border border-[#E2E8F0] bg-white" data-testid={`admin-search-${s.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-display text-lg text-[#2C4C3B]">{s.activity_name}</div>
                      <div className="text-xs text-[#52606D]">{fmtDate(s.created_at)}</div>
                    </div>
                    <div className="text-xs text-[#52606D] mt-1">
                      {s.disorder_name} · {s.communicator_stage_title} · {s.severity_label} · {s.device === "avaz" ? "Avaz" : "Generic"}
                      {s.location_label && <> · {s.location_label}</>}
                    </div>
                    <div className="mt-2 text-xs text-[#1A202C]"><strong>{s.rows.length}</strong> rows · core {s.ratio_core}% / fringe {s.ratio_fringe}%</div>
                    <a href={`/results/${s.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-[#C66A51] hover:text-[#A85639] text-sm font-medium mt-2" data-testid={`admin-search-open-${s.id}`}>
                      View full list ↗
                    </a>
                  </div>
                ))}
              </div>
            )}

            {detailTab === "goals" && (
              <div className="p-6 space-y-3" data-testid="admin-detail-goals">
                {detailLoading && <div className="text-[#52606D]">Loading…</div>}
                {!detailLoading && goals.length === 0 && <div className="text-[#52606D]" data-testid="admin-detail-goals-empty">No goal banks yet.</div>}
                {goals.map((g) => (
                  <div key={g.id} className="p-4 rounded-2xl border border-[#E2E8F0] bg-white" data-testid={`admin-goal-${g.id}`}>
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <div className="font-display text-lg text-[#2C4C3B]">{g.activity_name}</div>
                      <div className="text-xs text-[#52606D]">{fmtDate(g.created_at)}</div>
                    </div>
                    <div className="text-xs text-[#52606D] mt-1">
                      {g.disorder_name} · {g.communicator_stage_title} · {g.severity_label} · {g.device === "avaz" ? "Avaz" : "Generic"}
                    </div>
                    <div className="mt-2 text-xs text-[#1A202C]"><strong>{g.goals.length}</strong> SMART goals across domains</div>
                    <a href={`/goal-results/${g.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-[#C66A51] hover:text-[#A85639] text-sm font-medium mt-2" data-testid={`admin-goal-open-${g.id}`}>
                      <Target className="w-4 h-4 mr-1" />
                      View goal bank ↗
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const DetailRow = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-3 text-sm">
    <Icon className="w-4 h-4 text-[#52606D] mt-1" />
    <div className="flex-1">
      <div className="text-[10px] tracking-[0.15em] uppercase text-[#52606D]">{label}</div>
      <div className={`mt-0.5 text-[#1A202C] ${mono ? "font-mono text-xs break-all" : ""}`}>{value || "—"}</div>
    </div>
  </div>
);
